import type { Column } from "@shared/types/data-table"
import type { Enrollment } from "@shared/types/engagement.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createStatusColumn,
  createCurrencyColumn,
  createDateColumn,
  createTextColumn,
  createTruncatedColumn,
  createCustomColumn,
} from "./column-factories"

// Usage example for EnrollmentTable:
// const columns = getEnrollmentColumns('all')
// <BaseEntityTable columns={columns} ... />

export const ENROLLMENT_COLUMNS: Record<string, Column<Enrollment>> = {
  customer: createCustomColumn<Enrollment>(
    "customerName",
    "Customer",
    200,
    (enrollment) => (
      <div>
        <p className="font-medium text-sm">{enrollment.customerName}</p>
        <p className="text-xs text-muted-foreground">{enrollment.customerEmail}</p>
      </div>
    )
  ),
  campaign: createTruncatedColumn<Enrollment>(
    "campaignName",
    "Campaign",
    180,
    (enrollment) => enrollment.campaignName
  ),
  status: createStatusColumn<Enrollment>("enrollment"),
  tier: createTextColumn<Enrollment>("tier" as keyof Enrollment, "Tier", 100),
  pointsBalance: {
    ...createCustomColumn<Enrollment>(
      "pointsBalance",
      "Points Balance",
      120,
      (enrollment) => (
        <span className="font-medium text-sm tabular-nums text-right block">
          {enrollment.pointsBalance.toLocaleString()}
        </span>
      )
    ),
    align: "right",
  },
  lifetimeValue: createCurrencyColumn<Enrollment>("lifetimeValue" as keyof Enrollment, "Lifetime Value", 140),
  channel: createTextColumn<Enrollment>(
    "channel" as keyof Enrollment, "Channel", 100, undefined, undefined,
    (value: string) => value?.replace(/_/g, ' ') ?? ''
  ),
  lastActivity: createDateColumn<Enrollment>("lastActivityAt" as keyof Enrollment, "Last Activity", 150),
  enrolledAt: createDateColumn<Enrollment>("enrolledAt" as keyof Enrollment, "Enrolled", 150),
  pointsEarned: {
    ...createCustomColumn<Enrollment>(
      "pointsEarned",
      "Points Earned",
      120,
      (enrollment) => (
        <span className="text-sm tabular-nums text-right block">
          {enrollment.pointsEarned.toLocaleString()}
        </span>
      )
    ),
    align: "right",
  },
  pointsRedeemed: {
    ...createCustomColumn<Enrollment>(
      "pointsRedeemed",
      "Points Redeemed",
      130,
      (enrollment) => (
        <span className="text-sm tabular-nums text-right block">
          {enrollment.pointsRedeemed.toLocaleString()}
        </span>
      )
    ),
    align: "right",
  },
}

export const ENROLLMENT_STATUS_CONFIG = {
  all: ["customer", "campaign", "status", "tier", "pointsBalance", "lifetimeValue", "channel", "lastActivity"],
  active: ["customer", "campaign", "tier", "pointsBalance", "lifetimeValue", "channel", "lastActivity"],
  pending: ["customer", "campaign", "channel", "enrolledAt"],
  suspended: ["customer", "campaign", "tier", "pointsBalance", "lastActivity"],
} as const

export const getEnrollmentColumns = (status: string = 'all'): Column<Enrollment>[] => {
  const keys = ENROLLMENT_STATUS_CONFIG[status as keyof typeof ENROLLMENT_STATUS_CONFIG] || ENROLLMENT_STATUS_CONFIG.all
  return keys.map(key => ENROLLMENT_COLUMNS[key]).filter(Boolean)
}

export const getEnrollmentColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(
    ENROLLMENT_STATUS_CONFIG[status as keyof typeof ENROLLMENT_STATUS_CONFIG] || ENROLLMENT_STATUS_CONFIG.all
  )
  return Object.entries(ENROLLMENT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["customer", "status"].includes(key),
    order: index,
  }))
}
